import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@macs-frontend/core/base';
import { closeMatDialog } from '@macs-frontend/core/utils/mat-dialog';
import { makeSelectable, SelectSubscribe } from '@macs-frontend/decorators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromRoot from '../../../../reducers';

export const DEFAULT_CLOSE_REASON = 'DEFAULT_CLOSE_REASON';
export const CANCELLED = 'CANCELLED';

@Component({
  selector: 'md-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent
  extends BaseComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input() openOnInit = false;
  @Input() title?: string;
  @Input() content?: string;
  @Input() cancelBtnText = 'Cancel';
  @Input() confirmBtnText = 'Confirm';
  @Input() confirmLoaderType?: string;
  @Input() cancelLoaderType?: string;
  @Input() config?: MatDialogConfig;
  @Input() hideActions = false;
  @Input() confirmEnabled = true;
  @Input() close$?: Observable<void>;
  @Input() open$?: Observable<void>;
  @Input() showConfirmButton = true;
  @Input() showCancelButton = true;
  @Input() showLoader = false;

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();
  @Output() afterClose = new EventEmitter<string | null /* reason */>();

  // @ts-expect-error
  @ViewChild('template') template: TemplateRef<any>;

  @SelectSubscribe(fromRoot.loaderInProgressSelector)
  loaderInProgress: string[] = [];

  private dialogRef?: MatDialogRef<void>;

  constructor(
    private dialog: MatDialog,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<fromRoot.State>,
  ) {
    super();
    makeSelectable(this, { cdr, store$ });
  }

  ngOnInit(): void {
    if (this.close$) {
      this.close$.pipe(this.takeUntilDestroy()).subscribe(() => {
        this.close();
      });
    }

    if (this.open$) {
      this.open$.pipe(this.takeUntilDestroy()).subscribe(() => {
        this.open();
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.openOnInit) {
      this.open();
    }
  }

  open() {
    // eslint-disable-next-line functional/no-let
    let panelClass = this.config && this.config.panelClass;
    if (panelClass) {
      if (Array.isArray(panelClass)) {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        panelClass.push('md-modal-container');
      } else {
        panelClass = [panelClass, 'md-modal-container'];
      }
    } else {
      panelClass = ['md-modal-container'];
    }
    this.dialogRef = this.dialog.open(this.template, {
      ...(this.config || {}),
      panelClass,
    });
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(reason => {
        this.afterClose.emit(reason);
      });
  }

  close(reason: string = DEFAULT_CLOSE_REASON) {
    if (
      this.dialogRef &&
      (!this.cancelLoaderType ||
        !this.loaderInProgress.includes(this.cancelLoaderType)) &&
      (!this.confirmLoaderType || !this.loaderInProgress.includes(this.confirmLoaderType))
    ) {
      closeMatDialog(this.dialogRef, reason);
    }
  }

  ngOnDestroy(): void {
    this.close();
    super.ngOnDestroy();
  }

  handleCancel() {
    this.cancel.emit();
    this.close(CANCELLED);
  }
}
